import type { GraphqlJob } from "@noiman/shared-graphql";

export const SOURCE_VIEW_CTA_LABEL = "掲載元で見る ↗" as const;

export const JOB_FEATURE_FLAG_NAMES = [
  "JOBS_AGGREGATION_ENABLED",
  "JOBS_CRAWLER_ENABLED",
  "JOBS_UNCLAIMED_INDEXING_ENABLED",
  "JOBS_JOBPOSTING_SCHEMA_ENABLED",
  "JOBS_EMPLOYER_CLAIM_ENABLED",
  "JOBS_DIRECT_APPLY_CTA_ENABLED",
  "JOBS_JOB_SKILL_GRAPH_ENABLED",
  "JOBS_JOB_LEARNING_PATHS_ENABLED",
  "JOBS_JOB_DATA_LANDINGS_ENABLED",
  "JOBS_PERSONALIZATION_ENABLED",
] as const;

export type JobFeatureFlagName = (typeof JOB_FEATURE_FLAG_NAMES)[number];
export type JobFeatureFlags = Record<JobFeatureFlagName, boolean>;

export const DEFAULT_JOB_FEATURE_FLAGS: JobFeatureFlags = Object.freeze(
  Object.fromEntries(
    JOB_FEATURE_FLAG_NAMES.map((name) => [name, false]),
  ) as JobFeatureFlags,
);

export function readJobFeatureFlags(
  env: Record<string, unknown> | null | undefined,
): JobFeatureFlags {
  const runtime = env ?? {};
  return Object.fromEntries(
    JOB_FEATURE_FLAG_NAMES.map((name) => [name, runtime[name] === "true"]),
  ) as JobFeatureFlags;
}

export const JOBPOSTING_AUTHORIZATION_STATUSES = new Set([
  "owned",
  "employer_submitted",
  "claimed_authorized",
  "contractual_feed",
]);

export const JOB_AUTHORIZATION_SCOPES = [
  "company_profile",
  "all_jobs",
  "single_job",
  "full_copy",
  "logo",
  "images",
  "jobposting",
  "feed_ingestion",
  "direct_apply",
  "email_contact",
] as const;

export type JobAuthorizationScope = (typeof JOB_AUTHORIZATION_SCOPES)[number];

export function hasJobAuthorizationScope(
  subject: { authorizationScopes?: readonly string[] | null },
  scope: JobAuthorizationScope,
): boolean {
  return Array.isArray(subject.authorizationScopes)
    ? subject.authorizationScopes.includes(scope)
    : false;
}

export type SourceCta =
  | {
      mode: "source_view";
      label: typeof SOURCE_VIEW_CTA_LABEL;
      href: string;
    }
  | {
      mode: "direct_apply";
      label: "掲載元で応募方法を確認 ↗";
      href: string;
    }
  | { mode: "disabled" };

function safePublicHttpUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    const hostname = url.hostname.replace(/^\[|\]$/g, "").toLowerCase();
    const ipv4 = hostname.split(".").map(Number);
    const privateHost =
      hostname === "localhost" ||
      hostname.endsWith(".localhost") ||
      hostname === "::1" ||
      hostname.startsWith("fc") ||
      hostname.startsWith("fd") ||
      hostname.startsWith("fe80:") ||
      (ipv4.length === 4 &&
        (ipv4[0] === 10 ||
          ipv4[0] === 127 ||
          (ipv4[0] === 169 && ipv4[1] === 254) ||
          (ipv4[0] === 172 && ipv4[1] >= 16 && ipv4[1] <= 31) ||
          (ipv4[0] === 192 && ipv4[1] === 168)));
    if (
      (url.protocol !== "https:" && url.protocol !== "http:") ||
      url.username ||
      url.password ||
      !url.hostname ||
      privateHost
    ) {
      return null;
    }
    return url.toString();
  } catch {
    return null;
  }
}

export function sourceHostname(raw: string | null | undefined): string | null {
  const safe = safePublicHttpUrl(raw);
  return safe ? new URL(safe).hostname : null;
}

/**
 * Resolves the only outbound action rendered by public Jobs. The return value
 * deliberately contains no profile, email, resume, session, or candidate
 * payload; the browser performs a plain external navigation.
 */
export function resolveSourceCta(
  job: Pick<
    GraphqlJob,
    | "authorizationStatus"
    | "authorizationScopes"
    | "canonicalSourceUrl"
    | "officialApplyUrl"
    | "officialApplyUrlHealthy"
    | "sourceCtaMode"
    | "sourceUrlHealthy"
  >,
  flags: JobFeatureFlags,
): SourceCta {
  const sourceUrl = safePublicHttpUrl(job.canonicalSourceUrl);
  if (
    job.sourceCtaMode === "disabled" ||
    job.sourceUrlHealthy !== true ||
    !sourceUrl
  ) {
    return { mode: "disabled" };
  }

  const applyUrl = safePublicHttpUrl(job.officialApplyUrl);
  if (
    job.sourceCtaMode === "direct_apply" &&
    flags.JOBS_DIRECT_APPLY_CTA_ENABLED &&
    JOBPOSTING_AUTHORIZATION_STATUSES.has(job.authorizationStatus) &&
    hasJobAuthorizationScope(job, "direct_apply") &&
    job.officialApplyUrlHealthy === true &&
    applyUrl
  ) {
    return {
      mode: "direct_apply",
      label: "掲載元で応募方法を確認 ↗",
      href: applyUrl,
    };
  }

  return {
    mode: "source_view",
    label: SOURCE_VIEW_CTA_LABEL,
    href: sourceUrl,
  };
}

export function isFactsOnlyJob(
  job: Pick<GraphqlJob, "contentDisplayMode">,
): boolean {
  return job.contentDisplayMode === "facts_only";
}

export function canDisplayJobLicensedContent(
  job: Pick<
    GraphqlJob,
    "authorizationScopes" | "authorizationStatus" | "contentDisplayMode"
  >,
): boolean {
  return (
    !isFactsOnlyJob(job) &&
    JOBPOSTING_AUTHORIZATION_STATUSES.has(job.authorizationStatus) &&
    hasJobAuthorizationScope(job, "full_copy")
  );
}

const VERIFIED_COMPANY_CLAIM_STATUSES = new Set([
  "verified",
  "claimed_verified",
  "owned",
]);
const LICENSED_COMPANY_DESCRIPTION_MODES = new Set([
  "owned",
  "licensed",
  "employer_provided",
]);

export function isVerifiedCompany(company: { claimStatus: string }): boolean {
  return VERIFIED_COMPANY_CLAIM_STATUSES.has(company.claimStatus);
}

export function canDisplayCompanyNarrative(company: {
  authorizationScopes?: readonly string[] | null;
  claimStatus: string;
  descriptionMode: string;
}): boolean {
  return (
    isVerifiedCompany(company) &&
    LICENSED_COMPANY_DESCRIPTION_MODES.has(company.descriptionMode) &&
    hasJobAuthorizationScope(company, "company_profile") &&
    hasJobAuthorizationScope(company, "full_copy")
  );
}

export function canDisplayCompanyLogo(company: {
  authorizationScopes?: readonly string[] | null;
  claimStatus: string;
}): boolean {
  return (
    isVerifiedCompany(company) &&
    hasJobAuthorizationScope(company, "company_profile") &&
    hasJobAuthorizationScope(company, "logo")
  );
}

export function canDisplayCompanyImages(company: {
  authorizationScopes?: readonly string[] | null;
  claimStatus: string;
}): boolean {
  return (
    isVerifiedCompany(company) &&
    hasJobAuthorizationScope(company, "company_profile") &&
    hasJobAuthorizationScope(company, "images")
  );
}

function deadlineIsActive(raw: string | null | undefined, now: Date): boolean {
  if (!raw) return false;
  const deadline = new Date(raw);
  return (
    !Number.isNaN(deadline.getTime()) && deadline.getTime() > now.getTime()
  );
}

export function canEmitJobPosting(
  job: Pick<
    GraphqlJob,
    | "authorizationStatus"
    | "authorizationScopes"
    | "coreFieldsComplete"
    | "deadline"
    | "lifecycleStatus"
    | "officialApplyUrl"
    | "officialApplyUrlHealthy"
    | "structuredDataStatus"
    | "visibleContentMatchesSchema"
  >,
  flags: JobFeatureFlags,
  now = new Date(),
): boolean {
  return (
    flags.JOBS_JOBPOSTING_SCHEMA_ENABLED &&
    JOBPOSTING_AUTHORIZATION_STATUSES.has(job.authorizationStatus) &&
    hasJobAuthorizationScope(job, "jobposting") &&
    job.lifecycleStatus === "published" &&
    job.structuredDataStatus === "jobposting_eligible" &&
    job.coreFieldsComplete === true &&
    job.visibleContentMatchesSchema === true &&
    job.officialApplyUrlHealthy === true &&
    safePublicHttpUrl(job.officialApplyUrl) !== null &&
    deadlineIsActive(job.deadline, now)
  );
}

export function isUnclaimedJob(
  job: Pick<GraphqlJob, "authorizationStatus">,
): boolean {
  return !JOBPOSTING_AUTHORIZATION_STATUSES.has(job.authorizationStatus);
}

function verifiedWithinDays(
  raw: string | null | undefined,
  days: number,
  now: Date,
): boolean {
  if (!raw) return false;
  const verified = new Date(raw);
  if (Number.isNaN(verified.getTime())) return false;
  const age = now.getTime() - verified.getTime();
  return age >= 0 && age <= days * 86_400_000;
}

export function isJobIndexable(
  job: Pick<
    GraphqlJob,
    | "authorizationStatus"
    | "canonicalSourceUrl"
    | "coreFieldsComplete"
    | "duplicateOfJobId"
    | "lastVerifiedAt"
    | "learningGraphScore"
    | "lifecycleStatus"
    | "qualityScore"
    | "seoIndexStatus"
    | "sourceUrlHealthy"
  >,
  flags: JobFeatureFlags,
  now = new Date(),
): boolean {
  if (isUnclaimedJob(job) && !flags.JOBS_UNCLAIMED_INDEXING_ENABLED) {
    return false;
  }

  return (
    job.seoIndexStatus === "index" &&
    job.lifecycleStatus === "published" &&
    job.sourceUrlHealthy === true &&
    safePublicHttpUrl(job.canonicalSourceUrl) !== null &&
    job.coreFieldsComplete === true &&
    job.duplicateOfJobId == null &&
    verifiedWithinDays(job.lastVerifiedAt, 7, now) &&
    job.qualityScore >= 75 &&
    job.learningGraphScore > 0
  );
}

export function robotsForJob(
  job: Parameters<typeof isJobIndexable>[0],
  flags: JobFeatureFlags,
  now = new Date(),
): "index, follow" | "noindex, follow" {
  return isJobIndexable(job, flags, now) ? "index, follow" : "noindex, follow";
}

type DedupeJob = Pick<
  GraphqlJob,
  "duplicateOfJobId" | "id" | "location" | "salary" | "title" | "type"
> & {
  company: Pick<GraphqlJob["company"], "id">;
};

function normalizedFingerprint(job: DedupeJob): string {
  const normalizedTitle = job.title
    .normalize("NFKC")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
  return [
    job.company.id,
    normalizedTitle,
    job.type,
    job.location,
    job.salary?.min ?? "",
    job.salary?.max ?? "",
    job.salary?.currency ?? "",
    job.salary?.period ?? "",
  ].join("|");
}

function preferLowerPublicId<T extends DedupeJob>(a: T, b: T): T {
  const aId = Number(a.id);
  const bId = Number(b.id);
  if (Number.isFinite(aId) && Number.isFinite(bId)) {
    return aId <= bId ? a : b;
  }
  return a.id.localeCompare(b.id) <= 0 ? a : b;
}

/**
 * DB-marked duplicates are always removed. Exact legacy duplicates are also
 * collapsed as a safe Phase-0 fallback until the reviewed backfill is applied.
 */
export function dedupePublicJobs<T extends DedupeJob>(jobs: readonly T[]): T[] {
  const canonical = new Map<string, T>();
  for (const job of jobs) {
    if (job.duplicateOfJobId != null) continue;
    const fingerprint = normalizedFingerprint(job);
    const existing = canonical.get(fingerprint);
    canonical.set(
      fingerprint,
      existing ? preferLowerPublicId(existing, job) : job,
    );
  }
  return [...canonical.values()];
}

export function canonicalDuplicateFor<T extends DedupeJob>(
  job: T,
  allJobs: readonly T[],
): T | null {
  if (job.duplicateOfJobId != null) {
    return (
      allJobs.find(
        (candidate) => candidate.id === String(job.duplicateOfJobId),
      ) ?? null
    );
  }
  const canonical = dedupePublicJobs(allJobs).find(
    (candidate) =>
      normalizedFingerprint(candidate) === normalizedFingerprint(job),
  );
  return canonical && canonical.id !== job.id ? canonical : null;
}
